import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, Search } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useTransactions } from "@/hooks/useTransactions";
import {
  BudgetVsActualRow,
  BudgetStatus,
  BUDGET_STATUS_COLORS,
  BUDGET_STATUS_LABELS,
  calculateVariance,
  calculateVariancePercentage,
  determineBudgetStatus,
  formatCurrency,
  formatVariance,
} from "@/types/construction";

export function BudgetVsActualReport() {
  const { costCenters, isLoading: isLoadingCostCenters } = useCostCenters();
  const { transactions, isLoading: isLoadingTransactions } = useTransactions();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<BudgetStatus | "all">("all");
  const [sortBy, setSortBy] = useState<"name" | "variance">("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Calculate budget vs actual for each cost center with budgeted_cost
  const reportData: BudgetVsActualRow[] = useMemo(() => {
    if (!costCenters || !transactions) return [];

    return costCenters
      .filter((cc) => cc.is_active)
      .map((cc) => {
        // Calculate actual cost (only paid expenses)
        const actualCost = transactions
          .filter(
            (t) =>
              t.cost_center_id === cc.id &&
              t.type === "expense" &&
              t.is_paid === true
          )
          .reduce((sum, t) => sum + t.amount, 0);

        const variance = calculateVariance(cc.budgeted_cost, actualCost);
        const variancePercentage = calculateVariancePercentage(
          cc.budgeted_cost,
          actualCost
        );
        const status = determineBudgetStatus(variance, cc.budgeted_cost);

        return {
          workId: cc.id,
          workName: cc.name,
          workCode: cc.code ?? undefined,
          budgetedCost: cc.budgeted_cost,
          actualCost,
          variance,
          variancePercentage,
          status,
        };
      });
  }, [costCenters, transactions]);

  // Apply filters
  const filteredData = useMemo(() => {
    let data = [...reportData];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((row) =>
        row.workName.toLowerCase().includes(query) ||
        (row.workCode?.toString().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      data = data.filter((row) => row.status === statusFilter);
    }

    // Sort
    data.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "name") {
        comparison = a.workName.localeCompare(b.workName);
      } else if (sortBy === "variance") {
        comparison = a.variance - b.variance;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return data;
  }, [reportData, searchQuery, statusFilter, sortBy, sortDirection]);

  // Calculate summary stats
  const summary = useMemo(() => {
    const totalBudgeted = reportData.reduce(
      (sum, row) => sum + (row.budgetedCost || 0),
      0
    );
    const totalActual = reportData.reduce((sum, row) => sum + row.actualCost, 0);
    const overBudgetCount = reportData.filter(
      (row) => row.status === BudgetStatus.OVER_BUDGET
    ).length;
    const onBudgetCount = reportData.filter(
      (row) => row.status === BudgetStatus.ON_BUDGET
    ).length;
    const underBudgetCount = reportData.filter(
      (row) => row.status === BudgetStatus.UNDER_BUDGET
    ).length;

    return {
      totalBudgeted,
      totalActual,
      totalVariance: totalBudgeted - totalActual,
      overBudgetCount,
      onBudgetCount,
      underBudgetCount,
    };
  }, [reportData]);

  const handleExportCSV = () => {
    const headers = [
      "Obra",
      "Código",
      "Orçado",
      "Realizado",
      "Variância",
      "Variância %",
      "Status",
    ];
    const rows = filteredData.map((row) => [
      row.workName,
      row.workCode?.toString() || "",
      row.budgetedCost?.toString() || "0",
      row.actualCost.toString(),
      row.variance.toString(),
      row.variancePercentage.toFixed(2),
      BUDGET_STATUS_LABELS[row.status],
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `orcado-vs-realizado-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isLoading = isLoadingCostCenters || isLoadingTransactions;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando relatório...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Orçado vs. Realizado</h1>
          <p className="text-muted-foreground">
            Acompanhe o desempenho orçamentário das suas obras
          </p>
        </div>
        <Button onClick={handleExportCSV} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Exportar CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Orçado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.totalBudgeted)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Realizado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summary.totalActual)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Variância Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${
                summary.totalVariance >= 0 ? "text-green-600" : "text-red-600"
              }`}
            >
              {formatVariance(summary.totalVariance)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status das Obras</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="text-sm">
              <span className="text-green-600 font-medium">
                {summary.underBudgetCount}
              </span>{" "}
              abaixo
            </div>
            <div className="text-sm">
              <span className="text-yellow-600 font-medium">
                {summary.onBudgetCount}
              </span>{" "}
              no orçamento
            </div>
            <div className="text-sm">
              <span className="text-red-600 font-medium">
                {summary.overBudgetCount}
              </span>{" "}
              acima
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou código da obra..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <div className="w-full md:w-48">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as BudgetStatus | "all")}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value={BudgetStatus.UNDER_BUDGET}>
                    Abaixo do orçamento
                  </SelectItem>
                  <SelectItem value={BudgetStatus.ON_BUDGET}>
                    No orçamento
                  </SelectItem>
                  <SelectItem value={BudgetStatus.OVER_BUDGET}>
                    Acima do orçamento
                  </SelectItem>
                  <SelectItem value={BudgetStatus.NO_BUDGET}>
                    Sem orçamento
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Report Table */}
      <Card>
        <CardContent className="pt-6">
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma obra encontrada com os filtros aplicados
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Obra</TableHead>
                    <TableHead className="text-right">Orçado</TableHead>
                    <TableHead className="text-right">Realizado</TableHead>
                    <TableHead className="text-right">Variância</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((row) => (
                    <TableRow key={row.workId}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{row.workName}</div>
                          {row.workCode && (
                            <div className="text-sm text-muted-foreground">
                              Código: {row.workCode.toString().padStart(4, "0")}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.budgetedCost !== null
                          ? formatCurrency(row.budgetedCost)
                          : "Não orçado"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.actualCost)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            row.variance >= 0 ? "text-green-600" : "text-red-600"
                          }
                        >
                          {formatVariance(row.variance)}
                          {row.budgetedCost && row.budgetedCost > 0 && (
                            <span className="text-xs ml-1">
                              ({row.variancePercentage.toFixed(1)}%)
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={BUDGET_STATUS_COLORS[row.status]}>
                          {BUDGET_STATUS_LABELS[row.status]}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
